import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";
import { openBlob } from "@/lib/utils";
import type { LabelOrder, LabelOrderWithStatus, MetaPackResult, PackCompletionResult } from "@/services/fulfillment";

/** Odgovor packa; `members` postoji samo kod meta narudžbe (rezultat po članu). */
export type PackResult = PackCompletionResult & { members?: MetaPackResult["members"] };

export function useBusinessSettings(storeId: string | null) {
  return useQuery({
    queryKey: ["business-settings", storeId],
    queryFn: () => fetchJson<any>(`/api/fulfillment/settings?storeId=${storeId}`),
    enabled: !!storeId,
  });
}

export function useSaveBusinessSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) =>
      fetchJson("/api/fulfillment/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["business-settings"] }),
  });
}

export function usePickPackSettings(storeId: string | null) {
  return useQuery({
    queryKey: ["pick-pack-settings", storeId],
    queryFn: () => fetchJson<{ imageSize: number; locationFontSize: number; hideWarehouseName: boolean }>(`/api/fulfillment/pick-pack-settings?storeId=${storeId}`),
    enabled: !!storeId,
  });
}

export function useSavePickPackSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { storeId: string; imageSize: number; locationFontSize: number; hideWarehouseName: boolean }) =>
      fetchJson("/api/fulfillment/pick-pack-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pick-pack-settings"] }),
  });
}

/** `purpose: "pack"` — server odmah odbija meta narudžbu koja se još ne može spakovati. */
export function useLookupOrder(storeId: string | null, query: string, purpose?: "pack") {
  return useQuery({
    queryKey: ["fulfillment-lookup", storeId, query, purpose ?? null],
    queryFn: () => fetchJson<any>(`/api/fulfillment/lookup?storeId=${storeId}&q=${encodeURIComponent(query)}${purpose ? `&purpose=${purpose}` : ""}`),
    enabled: !!storeId && !!query,
    retry: false,
  });
}

export function useRecordPick() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, ...body }: { orderId: string; storeId: string; itemsJson: string; requestId: string }) =>
      fetchJson("/api/fulfillment/pick", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": requestId },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useFulfillOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orderId: string; storeId: string; itemsJson: string }) =>
      fetchJson<PackResult>("/api/fulfillment/pack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["label-orders"] });
      qc.invalidateQueries({ queryKey: ["label-orders-all"] });
      qc.invalidateQueries({ queryKey: ["order-shipments-bulk"] });
      // Spakovana meta narudžba (i završeni članovi) mijenja stranicu Spajanje pošiljki i oznake u listi.
      qc.invalidateQueries({ queryKey: ["meta-orders"] });
      qc.invalidateQueries({ queryKey: ["meta-candidates"] });
      qc.invalidateQueries({ queryKey: ["meta-badges"] });
    },
  });
}

export type CodSummary = {
  totalPending: number;
  totalReceivedThisMonth: number;
  pendingCount: number;
  totalReturned: number;
  returnedCount: number;
};

export type CodSummaryAll = {
  aggregated: CodSummary;
  perStore: Array<CodSummary & { storeId: string; storeName: string }>;
};

export function useCodEntries(storeId: string | null, filters: { status?: string; credentialId?: string; after?: number; before?: number; page?: number }) {
  const params = new URLSearchParams();
  if (storeId) params.set("storeId", storeId);
  if (filters.status) params.set("status", filters.status);
  if (filters.credentialId) params.set("credentialId", filters.credentialId);
  if (filters.after) params.set("after", String(filters.after));
  if (filters.before) params.set("before", String(filters.before));
  if (filters.page) params.set("page", String(filters.page));
  const scope = storeId ?? "all";
  return useQuery({
    queryKey: ["cod-ledger", scope, filters],
    queryFn: () => fetchJson<any>(`/api/fulfillment/cod?${params}`),
  });
}

export function useCodSummary(storeId: string | null) {
  const scope = storeId ?? "all";
  return useQuery({
    queryKey: ["cod-summary", scope],
    queryFn: () =>
      fetchJson<CodSummary | CodSummaryAll>(
        storeId ? `/api/fulfillment/cod/summary?storeId=${storeId}` : "/api/fulfillment/cod/summary",
      ),
  });
}

export function useMarkCodReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[] }) =>
      fetchJson("/api/fulfillment/cod/receive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cod-ledger"] });
      qc.invalidateQueries({ queryKey: ["cod-summary"] });
    },
  });
}

export function useScanCodReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; storeId?: string }) =>
      fetchJson("/api/fulfillment/cod/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cod-ledger"] });
      qc.invalidateQueries({ queryKey: ["cod-summary"] });
    },
  });
}

export function useBulkScanCodReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { codes: string[]; storeId?: string }) =>
      fetchJson("/api/fulfillment/cod/scan-bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cod-ledger"] });
      qc.invalidateQueries({ queryKey: ["cod-summary"] });
    },
  });
}

// Bez pollinga i bez globalnih 30 s staleTime: nalozi nastaju u drugim ekranima (bulk uvoz,
// veleprodaja), a status mijenjaju webhookovi i drugi radnici — invalidacija to ne vidi, pa se
// lista svježe čita pri svakom otvaranju stranice i povratku na tab (upit je jeftin).
export function useLabelOrders(storeId: string | null) {
  return useQuery({
    queryKey: ["label-orders", storeId],
    queryFn: () => fetchJson<LabelOrder[]>(
      `/api/fulfillment/labels/orders?storeId=${storeId}`
    ),
    enabled: !!storeId,
    staleTime: 0,
  });
}

export function useAllLabelOrders(storeId: string | null) {
  return useQuery({
    queryKey: ["label-orders-all", storeId],
    queryFn: () => fetchJson<LabelOrderWithStatus[]>(
      `/api/fulfillment/labels/all-orders?storeId=${storeId}`
    ),
    enabled: !!storeId,
    staleTime: 0,
  });
}

/** `metaIds` su meta narudžbe (jedna naljepnica po meta narudžbi), nikad u `orderIds`. */
export function useGenerateLabels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { orderIds: string[]; metaIds?: number[]; storeId: string }) => {
      const res = await fetch("/api/fulfillment/labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Greška pri generisanju naljepnica" }));
        throw new Error(err.error || "Greška pri generisanju naljepnica");
      }
      const blob = await res.blob();
      openBlob(blob, "naljepnice.pdf");
    },
    onSuccess: (_data, body) => {
      // Server bilježi "naljepnica štampana" za meta narudžbe — osvježi oznaku u oba taba i na Spajanju.
      if (!body.metaIds?.length) return;
      qc.invalidateQueries({ queryKey: ["label-orders"] });
      qc.invalidateQueries({ queryKey: ["label-orders-all"] });
      qc.invalidateQueries({ queryKey: ["meta-orders"] });
    },
  });
}

export function useGeneratePickupLabels() {
  return useMutation({
    mutationFn: async (body: { orderIds: string[]; storeId: string }) => {
      const res = await fetch("/api/fulfillment/pickup-labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Greška pri generisanju naljepnica" }));
        throw new Error(err.error || "Greška pri generisanju naljepnica");
      }
      const blob = await res.blob();
      openBlob(blob, "naljepnice-preuzimanje.pdf");
    },
  });
}

export type TestEmailResult = {
  ok: boolean;
  status: number;
  error?: string;
  hint?: string;
  detail?: Record<string, unknown>;
  config?: Record<string, unknown>;
};

export function useSendTestEmail() {
  return useMutation<TestEmailResult, Error, string>({
    mutationFn: async (email: string) => {
      const res = await fetch("/api/settings/test-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body: any = await res.json().catch(() => ({}));
      return {
        ok: res.ok && body?.ok === true,
        status: res.status,
        error: body?.error,
        hint: body?.hint,
        detail: body?.detail,
        config: body?.config,
      };
    },
  });
}
